import { forwardRef, useCallback, useId, type ChangeEvent } from 'react'
import { cn } from '../../utils.js'

export interface WeightInputProps {
  /** Current weight value */
  value: number
  /** Called when weight changes */
  onChange: (value: number) => void
  /** Weight unit label */
  unit?: string
  /** Minimum estimated weight */
  minWeight?: number
  /** Maximum estimated weight */
  maxWeight?: number
  /** Price per unit */
  pricePerUnit?: number
  /** Step increment */
  step?: number
  /** Visible label */
  label?: string
  /** Disabled state */
  disabled?: boolean
  /** Additional className */
  className?: string
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount)
}

/**
 * Weight input for variable-weight items.
 * Shows estimated range and updates estimated price as weight changes.
 */
export const WeightInput = forwardRef<HTMLDivElement, WeightInputProps>(function WeightInput(
  {
    value,
    onChange,
    unit = 'lb',
    minWeight = 0.5,
    maxWeight = 5,
    pricePerUnit,
    step = 0.25,
    label,
    disabled = false,
    className,
  },
  ref,
) {
  const id = useId()
  const sliderId = `${id}-slider`

  const clamp = useCallback(
    (v: number) => Math.min(maxWeight, Math.max(minWeight, Number(v.toFixed(2)))),
    [minWeight, maxWeight],
  )

  const handleSliderChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange(clamp(parseFloat(e.target.value)))
    },
    [clamp, onChange],
  )

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const parsed = parseFloat(e.target.value)
      if (!isNaN(parsed)) {
        onChange(clamp(parsed))
      }
    },
    [clamp, onChange],
  )

  const estimatedPrice = pricePerUnit ? value * pricePerUnit : null
  const minPrice = pricePerUnit ? minWeight * pricePerUnit : null
  const maxPrice = pricePerUnit ? maxWeight * pricePerUnit : null

  return (
    <div ref={ref} className={cn('flex flex-col gap-2', className)}>
      {label && (
        <label
          htmlFor={sliderId}
          className="text-sm font-medium text-[var(--color-text)]"
        >
          {label}
        </label>
      )}

      {/* Range display */}
      <p className="text-xs text-[var(--color-text-secondary)]">
        Estimated range: ~{minWeight} - {maxWeight} {unit}
      </p>

      {/* Slider */}
      <input
        id={sliderId}
        type="range"
        min={minWeight}
        max={maxWeight}
        step={step}
        value={value}
        onChange={handleSliderChange}
        disabled={disabled}
        aria-label={label ?? `Weight in ${unit}`}
        aria-valuemin={minWeight}
        aria-valuemax={maxWeight}
        aria-valuenow={value}
        aria-valuetext={`${value} ${unit}`}
        className={cn(
          'w-full h-2 rounded-full appearance-none cursor-pointer',
          'bg-[var(--color-neutral-200)] accent-[var(--color-primary)]',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        )}
      />

      {/* Value input + unit */}
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={value}
          onChange={handleInputChange}
          min={minWeight}
          max={maxWeight}
          step={step}
          disabled={disabled}
          aria-label={`Weight value in ${unit}`}
          className={cn(
            'w-20 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-text)]',
            'focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-0',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'transition-colors px-2 py-1.5 text-sm text-center',
          )}
        />
        <span className="text-sm text-[var(--color-text-secondary)]">{unit}</span>
      </div>

      {/* Price estimate */}
      {estimatedPrice != null && (
        <div className="flex flex-col gap-0.5" aria-live="polite">
          <p className="text-sm font-medium text-[var(--color-text)]">
            Est. {formatCurrency(estimatedPrice)}
          </p>
          {minPrice != null && maxPrice != null && (
            <p className="text-xs text-[var(--color-text-muted)]">
              Range: {formatCurrency(minPrice)} - {formatCurrency(maxPrice)}
            </p>
          )}
        </div>
      )}
    </div>
  )
})
